/**
 * useAuthorization Hook
 * 
 * React hook for authorization checks.
 * Provides convenient access to authorization functions
 * with user roles from the auth context.
 */

import { useMemo } from 'react';
import { Role } from './roles';
import { Permission } from './permissions';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  hasAnyRole,
} from './rbac';
import { useAuth } from '../context/AuthContext';

/**
 * Authorization hook return type
 */
export interface UseAuthorizationReturn {
  // Permission checks
  hasPermission: (permission: Permission | string) => boolean;
  hasAnyPermission: (permissions: (Permission | string)[]) => boolean;
  hasAllPermissions: (permissions: (Permission | string)[]) => boolean;
  
  // Role checks (use sparingly, prefer permissions)
  hasRole: (role: Role | string) => boolean;
  hasAnyRole: (roles: (Role | string)[]) => boolean;
  
  // User info
  userRoles: (Role | string)[];
  isAuthenticated: boolean;
  userId: number | null;
  partnerId: number | null;
}

/**
 * Hook for authorization checks
 * 
 * @returns Authorization functions and user info
 * 
 * @example
 * ```tsx
 * const { hasPermission } = useAuthorization();
 * 
 * if (hasPermission(PERMISSIONS.USER_CREATE)) {
 *   // Show create user button
 * }
 * ```
 */
export const useAuthorization = (): UseAuthorizationReturn => {
  const { userRoles = [], authenticated, userId = null, partnerId = null } = useAuth();

  // Memoize authorization functions to avoid recreating on every render
  const authorizationFunctions = useMemo(() => {
    return {
      hasPermission: (permission: Permission | string) =>
        hasPermission(userRoles, permission),
      
      hasAnyPermission: (permissions: (Permission | string)[]) =>
        hasAnyPermission(userRoles, permissions),
      
      hasAllPermissions: (permissions: (Permission | string)[]) =>
        hasAllPermissions(userRoles, permissions),
      
      hasRole: (role: Role | string) =>
        hasRole(userRoles, role),
      
      hasAnyRole: (roles: (Role | string)[]) =>
        hasAnyRole(userRoles, roles),
    };
  }, [userRoles]);

  return {
    ...authorizationFunctions,
    userRoles,
    isAuthenticated: authenticated === true,
    userId,
    partnerId,
  };
};

// Ensure this file is treated as a module
export {};

